import type { ToolCatalogSummary } from '@/lib/tool-catalog-types';
import type { ToolCatalogGroup, ToolCatalogItem, ToolKind } from '@/lib/tools';
import Link from 'next/link';

type ToolCatalogCardGridProps = {
  entries: ToolCatalogItem[];
  summaries?: Partial<Record<ToolKind, ToolCatalogSummary>>;
  emptyState?: {
    title: string;
    description: string;
  };
  showCtaAffordance?: boolean;
  ctaLabel?: string;
  className?: string;
  // When false the (client-loaded) rating is not rendered, so the card is fully
  // static and never repaints when catalog summaries arrive. Used by the /tools
  // catalog to keep the grid from flickering on load.
  showRating?: boolean;
};

const groupLabels: Record<ToolCatalogGroup, string> = {
  ai: 'AI',
  image: 'Image',
  video: 'Video',
  audio: 'Audio',
  pdf: 'PDF',
  privacy: 'Privacy',
  seller: 'Seller',
  utility: 'Utility',
};

export const emptyCatalogSummary: ToolCatalogSummary = {
  rating: {
    average: null,
    count: 0,
  },
  usageCount: 0,
};

export function ToolCatalogRating({ rating }: { rating: ToolCatalogSummary['rating'] }) {
  const hasRating = rating.average !== null && rating.count > 0;
  const averageValue = hasRating ? (rating.average ?? 0) : 0;
  const filledStars = Math.max(0, Math.min(5, Math.round(averageValue)));
  const starLabel = hasRating
    ? `${'★'.repeat(filledStars)}${'☆'.repeat(Math.max(0, 5 - filledStars))}`
    : null;
  const visibleLabel = hasRating
    ? `${averageValue.toFixed(1)} (${rating.count.toLocaleString()})`
    : 'Not rated yet';
  const ariaLabel = hasRating
    ? `${averageValue.toFixed(1)} out of 5 from ${rating.count.toLocaleString()} ratings`
    : 'Not rated yet';

  return (
    <div className="tool-app-catalog-rating" aria-label={ariaLabel}>
      {starLabel ? (
        <span aria-hidden="true" className="tool-app-catalog-rating-stars">
          {starLabel}
        </span>
      ) : null}
      <span className="tool-app-catalog-rating-label">{visibleLabel}</span>
    </div>
  );
}

function ToolCatalogCardCta({ label }: { label: string }) {
  return (
    <span className="tool-app-card-cta" aria-hidden="true">
      <svg viewBox="0 0 24 24" focusable="false">
        <path d="M8 6.5l9 5.5-9 5.5z" fill="currentColor" />
      </svg>
      <span>{label}</span>
    </span>
  );
}

export default function ToolCatalogCardGrid({
  entries,
  summaries,
  emptyState,
  showCtaAffordance = false,
  ctaLabel = 'Open tool',
  className,
  showRating = true,
}: ToolCatalogCardGridProps) {
  return (
    <div className={`tool-app-catalog-list ${className ?? ''}`.trim()}>
      {entries.map((item) => {
        const summary = summaries?.[item.slug] ?? emptyCatalogSummary;

        return (
          // Plain server-rendered Link (prefetch off) so the cards are in the SSR
          // HTML — reserving the grid height and avoiding a big layout shift.
          // Hover/touch prefetch is added via a delegated listener in
          // ToolAppCatalog so navigation stays instant without a per-card client
          // component (which previously stopped the cards from server-rendering).
          <Link
            className="tool-app-card"
            href={item.href}
            key={item.slug}
            prefetch={false}
            data-prefetch-card=""
          >
            <div className="tool-app-card-main">
              <div className={`tool-app-card-icon tool-app-card-icon-${item.catalog.accent}`}>
                <span>{item.catalog.iconLabel}</span>
              </div>

              <div className="tool-app-card-copy">
                <div className="tool-app-card-kicker-row">
                  <span className="tool-app-card-eyebrow">{item.eyebrow}</span>
                  <span className="tool-app-card-group">{groupLabels[item.catalog.group]}</span>
                  {item.catalog.featured ? (
                    <span className="tool-app-card-group tool-app-card-group-strong">Top pick</span>
                  ) : null}
                </div>

                <div className="tool-app-card-headline">
                  <div className="tool-app-card-titles">
                    <h3>{item.title}</h3>
                    <p className="tool-app-card-tagline">{item.catalog.tagline}</p>
                  </div>
                </div>

                <p className="tool-app-card-description">{item.description}</p>

                {showRating || showCtaAffordance ? (
                  <div className="tool-app-card-footer">
                    {showRating ? (
                      <div className="tool-app-card-metrics">
                        <ToolCatalogRating rating={summary.rating} />
                      </div>
                    ) : null}
                    {showCtaAffordance ? <ToolCatalogCardCta label={ctaLabel} /> : null}
                  </div>
                ) : null}
              </div>
            </div>
          </Link>
        );
      })}

      {entries.length === 0 && emptyState ? (
        <article className="tool-app-card tool-app-card-empty">
          <h3>{emptyState.title}</h3>
          <p>{emptyState.description}</p>
        </article>
      ) : null}
    </div>
  );
}
